import React, { useState, useCallback, useEffect } from "react";

import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input } from "antd";
import Highlighter from "react-highlight-words";

import NoResultsGIF from "../../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import { allCheckedTrue, initializeCheckedStates, updateCheckedStatesForUUID } from "./ExportCOAAllFilterHelper";

import pumpkinGIF from "../../../../../assets/images/environment/pumpkin.gif";

import "./ExportCOAAllFilter.css";

export default function ExportCOAAllFilter({
  handleToggleAllFilterDrawer, envCOAData, setSearch, search, setFilteredSamples, filteredSamples, loading, setController, controller, checkedStates, setCheckedStates, selectAll, setSelectAll,
}) {
  const firstKey = (envCOAData && Object.keys(envCOAData)[0]) || "";
  const [activeButtonId, setActiveButtonId] = useState(firstKey); // Active key should be first key of filter
  const [searchValue, setSearchValue] = useState(search);

  useEffect(() => {
    if (Object.keys(filteredSamples).length === 0) {
      setCheckedStates(initializeCheckedStates(envCOAData, false));
    }
  }, [filteredSamples, envCOAData, setCheckedStates]);

  const handleOnChange = (event, nestedKey) => {
    const { checked, value: uuidArray } = event.target;
    let sampleIds = envCOAData[activeButtonId];
    const newCheckedStates = { ...checkedStates };

    // Access nested keys to reach sampleIds
    nestedKey.forEach((k) => {
      sampleIds = sampleIds?.[k];
    });

    // Create a unique key string for the checkbox being changed
    const currentKeyString = nestedKey.join("-");

    // Handle filteredSamples state
    if (checked) {
      // Get the sample IDs from the sampleIds array
      const selectedSampleIds = sampleIds.filter((sampleId) => envCOAData.samples[sampleId]);
      const newSamples = selectedSampleIds.reduce((acc, sampleId) => {
        if (!filteredSamples[sampleId]) {
          acc[sampleId] = envCOAData.samples[sampleId];
        }
        return acc;
      }, {});
      setFilteredSamples((prevSamples) => {
        const updatedSamples = { ...newSamples, ...prevSamples };
        // After updating filteredSamples, call updateCheckedStatesForUUID
        uuidArray.forEach((uuid) => {
          updateCheckedStatesForUUID(uuid, activeButtonId, newCheckedStates, checked, envCOAData, setCheckedStates, updatedSamples);
        });
        return updatedSamples;
      });
    } else {
      setFilteredSamples((prevSamples) => {
        const updatedSamples = { ...prevSamples };
        sampleIds.forEach((sampleId) => delete updatedSamples[sampleId]);
        // After updating filteredSamples, call updateCheckedStatesForUUID
        uuidArray.forEach((uuid) => {
          updateCheckedStatesForUUID(uuid, activeButtonId, newCheckedStates, checked, envCOAData, setCheckedStates, updatedSamples);
        });
        return updatedSamples;
      });
    }

    newCheckedStates[activeButtonId][currentKeyString] = { checked, indeterminate: false };
    setCheckedStates(newCheckedStates);
    setSelectAll(allCheckedTrue(newCheckedStates));
  };

  const handleNestedFilterAndRenderFilterKeys = (obj, nestedKey = [], searchMatch = false) => {
    // Helper function to check if any key matches the search term
    const hasMatchingDescendant = (subObj) => Object.keys(subObj).some((subKey) => {
      if (typeof subObj[subKey] === "object" && !Array.isArray(subObj[subKey])) {
        return hasMatchingDescendant(subObj[subKey]);
      }
      return Array.isArray(subObj[subKey]) && subKey.toLowerCase().includes(search.toLowerCase());
    });

    const result = Object.keys(obj).map((key) => {
      // If current key is an object, check if it has a matching descendant
      if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
        if (search && !hasMatchingDescendant(obj[key]) && !key.toLowerCase().includes(search.toLowerCase())) {
          return null; // Skip rendering if no matches
        }
        return (
          key ? (
            <div className="ExportCOAAllFilter__Section__Div" key={key}>
              <div className="ExportCOAAllFilter__Section__Heading">
                <Highlighter
                  highlightClassName="searchHighlight"
                  searchWords={[search]}
                  autoEscape
                  textToHighlight={key}
                />
              </div>
              {/* Recursively render nested objects */}
              {handleNestedFilterAndRenderFilterKeys(obj[key], [...nestedKey, key], true)}
            </div>
          ) : null
        );
      }

      // If the current key is an array, check if it includes the search term
      if (Array.isArray(obj[key])) {
        const baseKey = [...nestedKey, key].join("-");
        if (search && !key.toLowerCase().includes(search.toLowerCase()) && !searchMatch) {
          return null; // Skip checkbox if key doesn't match search
        }
        return (
          <Checkbox
            key={key}
            className="ExportCOAAllFilter__CheckBox"
            checked={checkedStates[activeButtonId]?.[baseKey]?.checked}
            onChange={(event) => handleOnChange(event, [...nestedKey, key])}
            value={obj[key]}
            indeterminate={checkedStates[activeButtonId]?.[baseKey]?.indeterminate}
          >
            <Highlighter
              highlightClassName="searchHighlight"
              searchWords={[search]}
              autoEscape
              textToHighlight={key}
            />
          </Checkbox>
        );
      }
      return null;
    });
    // Check if all items in `result` are `null` and return `<NoResultsGIF />` if so
    if (result.every((item) => item === null)) {
      return <NoResultsGIF image={pumpkinGIF} message="No data" className="pumpkinGIF" />;
    }

    return result;
  };

  const handleSelectAll = useCallback((event) => {
    const { checked } = event.target;
    setSelectAll(checked);
    setFilteredSamples(checked ? envCOAData.samples : {});
    setCheckedStates(initializeCheckedStates(envCOAData, checked));
  }, [envCOAData, setFilteredSamples, setSelectAll, setCheckedStates]);

  const handleSearch = (value) => setSearch(value);

  // Update the search handler to manage AbortController
  const handleSearchWithAbort = (value) => {
    if (controller) {
      controller.abort();
    }

    const newController = new AbortController();
    setController(newController);

    handleSearch(value);
  };

  const handleClickFilterButton = (id) => {
    setActiveButtonId(id);
  };

  return (
    <div className="ExportCOAAllFilter__Container">
      {Object.keys(envCOAData).length ? (
        <>
          <div className="ExportCOAAllFilter__Header">
            <span>Filter locations by</span>
            <CloseOutlined
              className="ExportCOAAllFilter__CloseIcon"
              aria-label="Close filter"
              onClick={handleToggleAllFilterDrawer}
            />
          </div>
          <div className="ExportCOAAllFilter__div">
            <div className="ExportCOAAllFilter__Search">
              <Input.Search
                onSearch={handleSearchWithAbort}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value); // Update search state with the input value
                  if (!value) {
                    handleSearch(""); // Call handleSearch("") if the input is cleared
                  }
                }}
                className="SubmissionsSearch__Input"
                size="large"
                placeholder="Search"
                value={searchValue}
              />
            </div>
            <div className="ExportCOAAllFilter__Btn__div">
              <div className="ExportCOAAllFilter__Btn">
                {Object.keys(envCOAData).slice(0, -1).map((key, index) => (
                  <Button
                    key={index}
                    className={activeButtonId === key ? "ExportCOAAllFilter__Active__Button" : "ExportCOAAllFilter__Button"}
                    onClick={() => handleClickFilterButton(key)}
                    htmlType="button"
                  >
                    {key}
                  </Button>
                ))}
              </div>
              <hr className="ExportCOAAllFilter__Hr" />
              <Checkbox
                checked={selectAll}
                className="ExportCOAAllFilter__SelectAll__CheckBox"
                onChange={handleSelectAll}
                disabled={loading}
              >
                Select All
              </Checkbox>
            </div>
            <div className="ExportCOAAllFilter__Checkbox__div">
              {loading ? <LoadingOutlined spin className="ExportCOADataPreview__LoadingIcon" /> : (
                <StyledScrollbar>
                  <div className="ExportCOAAllFilter__Checkbox__List">
                    {Object.keys(envCOAData[activeButtonId]).length === 0
                      ? <NoResultsGIF image={pumpkinGIF} message="No data" className="pumpkinGIF" />
                      : handleNestedFilterAndRenderFilterKeys(envCOAData[activeButtonId])}
                  </div>
                </StyledScrollbar>
              )}
            </div>
          </div>
        </>
      ) : !loading && (
        <NoResultsGIF image={pumpkinGIF} message="No data" className="pumpkinGIF" />
      )}
    </div>
  );
}
